<template>
  <div v-if="canView" class="px-4 py-3 text-sm">
    <div class="flex gap-2 mb-2">
      <button
        v-for="opt in groupings"
        :key="opt.key"
        @click="activeGrouping = opt.key"
        class="px-2 py-1 text-xs rounded"
        :class="activeGrouping === opt.key ? 'bg-primary-100 text-primary-800 font-medium' : 'text-gray-500 hover:bg-gray-100'"
      >{{ opt.label }}</button>
    </div>
    <div v-if="loading" class="text-gray-400">Loading...</div>
    <table v-else class="w-full text-sm">
      <thead>
        <tr class="text-left text-gray-500 text-xs uppercase tracking-wider">
          <th class="py-1 pr-4">{{ groupColumnLabel }}</th>
          <th v-for="m in METRICS" :key="m.id" class="py-1 pr-6" style="min-width: 220px;">{{ m.label }}</th>
        </tr>
      </thead>
      <tbody>
        <!-- Group rows (cohort/pod): bar height scales with attribution -->
        <tr
          v-for="label in groupLabels"
          :key="label"
          class="border-t border-gray-100 transition-colors"
          :class="selectedLabel === label ? 'bg-primary-50' : 'hover:bg-gray-50'"
        >
          <td
            class="py-2 pr-4"
            :class="entityIdByLabel[label] ? 'cursor-pointer' : ''"
            @click="selectRow(label)"
          >
            <div class="font-medium text-gray-700 flex items-center gap-1.5">
              {{ label }}
              <span class="text-xs font-normal text-gray-400 tabular-nums">({{ num(attributedFor(label)) }})</span>
            </div>
          </td>
          <td
            v-for="m in METRICS"
            :key="m.id"
            class="py-2 pr-6"
            :class="entityIdByLabel[label] && rowFor(m.id, label) ? 'cursor-pointer' : ''"
            @click="selectInitiative(label, m)"
          >
            <div v-if="rowFor(m.id, label)" class="flex items-center gap-1.5">
              <span class="text-[10px] text-gray-400 tabular-nums w-8 text-right shrink-0">{{ axisMinLabel(m.id) }}</span>
              <div class="relative flex-1 bg-gray-100 rounded-sm overflow-hidden" :style="{ minWidth: '140px', height: barHeight(attributedFor(label)) + 'px' }">
                <!-- projected gain to come (neutral) -->
                <div v-if="rowFor(m.id, label).projected != null" class="absolute inset-y-0 bg-neutral-200"
                     :style="{ left: barPct(m.id, rowFor(m.id, label).current) + '%', width: Math.max(0, barPct(m.id, rowFor(m.id, label).projected) - barPct(m.id, rowFor(m.id, label).current)) + '%' }"></div>
                <!-- green/red shade: projection above/short of last-yr final -->
                <div v-for="seg in beatSegments(m.id, rowFor(m.id, label))" :key="'b'+seg.left" class="absolute inset-y-0" :class="seg.cls" :style="{ left: seg.left + '%', width: seg.width + '%' }"></div>
                <!-- current YTD: solid fill -->
                <div class="absolute inset-y-0 left-0 rounded-l-sm bg-neutral-500" :style="{ width: barPct(m.id, rowFor(m.id, label).current) + '%' }"></div>
                <div v-if="rowFor(m.id, label).lastYearFinal != null" class="absolute inset-y-0 w-0.5 bg-neutral-900 z-10" :style="{ left: barPct(m.id, rowFor(m.id, label).lastYearFinal) + '%' }" title="Last Yr Final"></div>
                <div v-if="rowFor(m.id, label).projected != null" class="absolute inset-y-0 w-0.5 z-10" :class="rowFor(m.id, label).projected >= (rowFor(m.id, label).lastYearFinal ?? 0) ? 'bg-emerald-600' : 'bg-red-500'" :style="{ left: barPct(m.id, rowFor(m.id, label).projected) + '%' }" title="Projected EOY"></div>
                <span class="absolute top-0 h-full flex items-center text-[11px] font-semibold tabular-nums pointer-events-none" :class="valueFitsInBar(m.id, rowFor(m.id, label)) ? 'text-white' : 'text-neutral-900'" :style="valueLabelStyle(m.id, rowFor(m.id, label))">{{ pct(rowFor(m.id, label).current) }}</span>
              </div>
              <span class="text-[10px] text-gray-400 tabular-nums w-8 shrink-0">{{ axisMaxLabel(m.id) }}</span>
              <!-- engagement trajectory dot for this metric's initiative -->
              <span class="w-2 h-2 rounded-full shrink-0" :style="{ backgroundColor: trajectoryDot(label, m.id).hex }" :title="trajectoryTitle(label, m.id)"></span>
            </div>
            <span v-else class="text-gray-300 text-xs">—</span>
          </td>
        </tr>

        <!-- Tenant total: separated below, regular-size bar, distinct color -->
        <tr class="border-t-2 border-gray-300 font-semibold">
          <td class="py-2 pr-4">
            <div class="font-medium text-gray-800 flex items-center gap-1.5">
              All (Tenant)
              <span class="text-xs font-normal text-gray-400 tabular-nums">({{ num(tenantAttributed) }})</span>
            </div>
          </td>
          <td v-for="m in METRICS" :key="m.id" class="py-2 pr-6">
            <div v-if="tenantRowFor(m.id)" class="flex items-center gap-1.5">
              <span class="text-[10px] text-gray-400 tabular-nums w-8 text-right shrink-0">{{ axisMinLabel(m.id) }}</span>
              <div class="relative flex-1 bg-gray-100 rounded-sm overflow-hidden" :style="{ minWidth: '140px', height: '16px' }">
                <div v-if="tenantRowFor(m.id).projected != null" class="absolute inset-y-0 bg-primary-200"
                     :style="{ left: barPct(m.id, tenantRowFor(m.id).current) + '%', width: Math.max(0, barPct(m.id, tenantRowFor(m.id).projected) - barPct(m.id, tenantRowFor(m.id).current)) + '%' }"></div>
                <div v-for="seg in beatSegments(m.id, tenantRowFor(m.id))" :key="'tb'+seg.left" class="absolute inset-y-0" :class="seg.cls" :style="{ left: seg.left + '%', width: seg.width + '%' }"></div>
                <div class="absolute inset-y-0 left-0 rounded-l-sm bg-primary-500" :style="{ width: barPct(m.id, tenantRowFor(m.id).current) + '%' }"></div>
                <div v-if="tenantRowFor(m.id).lastYearFinal != null" class="absolute inset-y-0 w-0.5 bg-neutral-900 z-10" :style="{ left: barPct(m.id, tenantRowFor(m.id).lastYearFinal) + '%' }" title="Last Yr Final"></div>
                <div v-if="tenantRowFor(m.id).projected != null" class="absolute inset-y-0 w-0.5 z-10" :class="tenantRowFor(m.id).projected >= (tenantRowFor(m.id).lastYearFinal ?? 0) ? 'bg-emerald-600' : 'bg-red-500'" :style="{ left: barPct(m.id, tenantRowFor(m.id).projected) + '%' }" title="Projected EOY"></div>
                <span class="absolute top-0 h-full flex items-center text-[11px] font-semibold tabular-nums pointer-events-none" :class="valueFitsInBar(m.id, tenantRowFor(m.id)) ? 'text-white' : 'text-neutral-900'" :style="valueLabelStyle(m.id, tenantRowFor(m.id))">{{ pct(tenantRowFor(m.id).current) }}</span>
              </div>
              <span class="text-[10px] text-gray-400 tabular-nums w-8 shrink-0">{{ axisMaxLabel(m.id) }}</span>
            </div>
            <span v-else class="text-gray-300 text-xs">—</span>
          </td>
        </tr>
      </tbody>
    </table>
    <div v-if="!loading" class="flex items-center gap-4 mt-2 text-[10px] text-gray-500">
      <span class="flex items-center gap-1"><span class="w-2.5 h-2 bg-neutral-500 inline-block"></span>Current YTD</span>
      <span class="flex items-center gap-1"><span class="w-2.5 h-2 bg-neutral-200 inline-block"></span>Projected gain to come</span>
      <span class="flex items-center gap-1"><span class="w-0.5 h-2.5 bg-neutral-900 inline-block"></span>Last Yr Final (line to beat)</span>
      <span class="flex items-center gap-1"><span class="w-2.5 h-2 bg-emerald-400/60 inline-block"></span>Projected above last yr</span>
      <span class="flex items-center gap-1"><span class="w-2.5 h-2 bg-red-400/50 inline-block"></span>Projected short of last yr</span>
      <span class="flex items-center gap-1 pl-2 border-l border-gray-200">
        <span class="text-gray-400">Engagement:</span>
        <span class="w-2 h-2 rounded-full inline-block" :style="{ backgroundColor: TRAJECTORY.ahead.hex }"></span>Ahead
        <span class="w-2 h-2 rounded-full inline-block" :style="{ backgroundColor: TRAJECTORY.on_track.hex }"></span>On pace
        <span class="w-2 h-2 rounded-full inline-block" :style="{ backgroundColor: TRAJECTORY.behind.hex }"></span>Behind
        <span class="w-2 h-2 rounded-full inline-block" :style="{ backgroundColor: TRAJECTORY.mixed.hex }"></span>Mixed
      </span>
    </div>

    <!-- Drill-in: the cohort engagement rollup cards for the selected grouping row (one per
         initiative), rolled up across that grouping's child practices (Kairon Intelligence). -->
    <div v-if="selectedEntityId" class="mt-4 pt-4 border-t border-gray-100">
      <div class="flex items-center justify-between mb-2">
        <div class="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
          {{ selectedLabel }} · Engagement Rollup
          <span v-if="selectedInitiativeLabel" class="text-xs font-normal text-gray-400">— {{ selectedInitiativeLabel }}</span>
          <font-awesome-icon
            v-if="rollupLoading"
            :icon="['fas', 'circle-notch']"
            class="text-gray-400 animate-spin ml-1"
          />
        </div>
        <button class="text-xs text-gray-400 hover:text-gray-600" @click="clearSelection">
          <font-awesome-icon :icon="['fas', 'xmark']" class="mr-1" />Close
        </button>
      </div>
      <CohortRollupCard
        :entity-id="selectedEntityId"
        :grouping-name="selectedLabel"
        :initiatives="selectedInitiatives"
        @loading="rollupLoading = $event"
      />
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faXmark, faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import CohortRollupCard from '@/components/InsightsNew/CohortRollupCard.vue';

library.add(faXmark, faCircleNotch);

// Metrics shown side by side. initiativeKey pairs each metric to its
// Engagement Graph initiative (nodes carry initiative_key + integer metric_ids;
// match by key, not "metric_ids contains N" — one node is mistagged). The
// cohort-summary endpoint returns trajectory keyed by initiative_key.
const METRICS = [
  { id: 125, label: 'AWV+ YTD', initiativeKey: 'awv_completion' },
  { id: 126, label: 'HCC Recapture (2Y)', initiativeKey: 'risk_documentation' },
  { id: 127, label: 'TOC 10-Day Follow-Up', initiativeKey: 'transitions_of_care' },
  { id: 18, label: 'SPV (3-Mo Seen)', initiativeKey: 'visit_coverage' },
];

// The engagement-trajectory color language (shared with the Engagement Tracker):
// ahead = green, on track = blue, behind = red, mixed = amber, none = gray.
const TRAJECTORY = {
  ahead: { label: 'Ahead of pace', hex: '#389A6C' },
  on_track: { label: 'On pace', hex: '#4573D2' },
  behind: { label: 'Behind pace', hex: '#D53D3D' },
  mixed: { label: 'Mixed', hex: '#F1BD6C' },
};
const NO_TRAJECTORY = { label: 'No engagement signal', hex: '#D2D2D3' };

// Vytalize entity groupings (tenant 2). Each grouping is ONE entity_type; the
// metric engine returns every entity of that type in a single /run_multi call
// (grouped server-side via the crosswalk), so we split the rows by entity_name.
const GROUPINGS = {
  cohort: {
    label: 'By Start Cohort',
    columnLabel: 'Start Cohort',
    entityType: 'Start Year',
    order: ['2019', '2020', '2021', '2022', '2023', '2024', '2025', '2026'],
  },
  pod: {
    label: 'By Pod',
    columnLabel: 'Pod',
    entityType: 'Pods',
    order: ['South', 'IPANY', 'West', 'Midwest', 'NY/NJ', 'Northeast', 'Kansas', 'Southeast'],
  },
  ptm: {
    label: 'By PTM',
    columnLabel: 'PTM',
    entityType: 'PTM',
    order: [],
  },
};

export default {
  name: 'ExecSummaryPlaceholder',
  components: { FontAwesomeIcon, CohortRollupCard },
  data() {
    return {
      loading: true,
      // rowsByMetric[metricId] = { groups: {label -> row}, tenant: row }
      rowsByMetric: {},
      // Resolved row labels for groupings whose GROUPINGS[key].order is empty
      // (PTM has no fixed roster, unlike start-year cohorts or pods) — derived
      // from whichever entity_names the crosswalk actually returns per fetch.
      dynamicGroupLabels: [],
      // engagementByName[groupName] = { name, trajectory_by_initiative, blockers, actions }
      engagementByName: {},
      // label → grouping entity_id, captured from cohort-summary; drives the rollup card drill-in.
      entityIdByLabel: {},
      // the grouping row the user drilled into (shows its rollup cards below); null = none.
      selectedLabel: null,
      // the focused initiative_key (clicking one metric bar); null = show the whole row's initiatives.
      selectedInitiative: null,
      // true while the CohortRollupCard is fetching (drives the header spinner).
      rollupLoading: false,
      activeGrouping: 'cohort',
      groupings: Object.entries(GROUPINGS).map(([key, g]) => ({ key, label: g.label })),
      METRICS,
      TRAJECTORY,
    };
  },
  computed: {
    // The grouping entity_id for the drilled-in row (null if none / not resolvable) — feeds the
    // rollup card. Resolved from the cohort-summary label→id map captured during fetch.
    selectedEntityId() {
      return this.selectedLabel ? (this.entityIdByLabel[this.selectedLabel] || null) : null;
    },
    // Initiative filter passed to the rollup card: one key when a metric bar was clicked,
    // empty (= all initiatives for the row) when the row name was clicked.
    selectedInitiatives() {
      return this.selectedInitiative ? [this.selectedInitiative] : [];
    },
    // Human label of the focused initiative (from METRICS) for the drill-in header.
    selectedInitiativeLabel() {
      if (!this.selectedInitiative) return null;
      return METRICS.find(m => m.initiativeKey === this.selectedInitiative)?.label || null;
    },
    // Visible to Kairon superusers and tenant admins. Reuses the existing
    // tenant/isTenantAdmin role getter — no per-widget feature flag.
    canView() {
      return (
        this.$store.getters['auth/current_user']?.is_superuser ||
        this.$store.getters['tenant/isTenantAdmin'] ||
        false
      );
    },
    groupColumnLabel() {
      return GROUPINGS[this.activeGrouping].columnLabel;
    },
    groupLabels() {
      const fixed = GROUPINGS[this.activeGrouping].order;
      return fixed.length ? fixed : this.dynamicGroupLabels;
    },
    tenantAttributed() {
      for (const m of METRICS) {
        const t = this.rowsByMetric[m.id]?.tenant;
        if (t?.attributed != null) return t.attributed;
      }
      return null;
    },
    maxAttribution() {
      let mx = 0;
      for (const label of this.groupLabels) {
        const a = this.attributedFor(label);
        if (a) mx = Math.max(mx, a);
      }
      return mx;
    },
  },
  watch: {
    activeGrouping() {
      this.clearSelection();   // clear the drill-in when switching cohort↔pod
      this.fetchData();
    },
  },
  methods: {
    // Click the row name → show ALL initiatives for that grouping row. Click again to close.
    selectRow(label) {
      if (!this.entityIdByLabel[label]) return;   // no resolvable grouping entity → not clickable
      if (this.selectedLabel === label && !this.selectedInitiative) {
        this.clearSelection();   // same whole-row toggled again → close
        return;
      }
      this.selectedLabel = label;
      this.selectedInitiative = null;
    },
    // Click one metric bar → drill into JUST that metric's initiative for the grouping row.
    // Clicking the same focused initiative again closes the drill-in.
    selectInitiative(label, metric) {
      if (!this.entityIdByLabel[label] || !this.rowFor(metric.id, label)) return;
      if (this.selectedLabel === label && this.selectedInitiative === metric.initiativeKey) {
        this.clearSelection();
        return;
      }
      this.selectedLabel = label;
      this.selectedInitiative = metric.initiativeKey;
    },
    clearSelection() {
      this.selectedLabel = null;
      this.selectedInitiative = null;
    },
    pct(v) {
      if (v === null || v === undefined) return '—';
      return `${(v * 100).toFixed(1)}%`;
    },
    num(v) {
      if (v === null || v === undefined) return '—';
      return v.toLocaleString();
    },
    rowFor(metricId, label) {
      return this.rowsByMetric[metricId]?.groups?.[label] || null;
    },
    tenantRowFor(metricId) {
      return this.rowsByMetric[metricId]?.tenant || null;
    },
    attributedFor(label) {
      for (const m of METRICS) {
        const r = this.rowFor(m.id, label);
        if (r?.attributed != null) return r.attributed;
      }
      return null;
    },
    // Per-metric auto axis: floor ~10% below the smallest value across that
    // metric's rows, ceiling ~10% above the largest. Each metric scales itself.
    axisRange(metricId) {
      const data = this.rowsByMetric[metricId];
      if (!data) return { min: 0, max: 1 };
      const vals = [];
      const collect = r => { if (r) [r.current, r.lastYearEfctv, r.lastYearFinal, r.projected].forEach(v => { if (v != null) vals.push(v); }); };
      Object.values(data.groups || {}).forEach(collect);
      collect(data.tenant);
      if (!vals.length) return { min: 0, max: 1 };
      const lo = Math.min(...vals), hi = Math.max(...vals);
      const pad = Math.max(0.02, (hi - lo) * 0.1);
      return { min: Math.max(0, lo - pad), max: Math.min(1, hi + pad) };
    },
    axisMinLabel(metricId) { return `${Math.round(this.axisRange(metricId).min * 100)}%`; },
    axisMaxLabel(metricId) { return `${Math.round(this.axisRange(metricId).max * 100)}%`; },
    barPct(metricId, v) {
      if (v === null || v === undefined) return 0;
      const { min, max } = this.axisRange(metricId);
      if (max <= min) return 0;
      return Math.min(Math.max(((v - min) / (max - min)) * 100, 0), 100);
    },
    barHeight(attributed) {
      const MIN_H = 20, MAX_H = 72;
      const max = this.maxAttribution;
      if (!max || !attributed) return MIN_H;
      return Math.round(MIN_H + (MAX_H - MIN_H) * (attributed / max));
    },
    // Shade the band between last-yr-final and projected: green where projected
    // clears last year (beating), red where it falls short (the gap to last year).
    beatSegments(metricId, row) {
      if (!row || row.projected == null || row.lastYearFinal == null) return [];
      const proj = this.barPct(metricId, row.projected);
      const last = this.barPct(metricId, row.lastYearFinal);
      if (proj >= last) {
        return [{ left: last, width: proj - last, cls: 'bg-emerald-400/60' }];
      }
      return [{ left: proj, width: last - proj, cls: 'bg-red-400/50' }];
    },
    valueFitsInBar(metricId, row) {
      return row && this.barPct(metricId, row.current) > 35;
    },
    valueLabelStyle(metricId, row) {
      const p = row ? this.barPct(metricId, row.current) : 0;
      if (p > 35) return { left: '4px', width: (p - 1) + '%', justifyContent: 'flex-end', paddingRight: '4px' };
      return { left: p + '%', paddingLeft: '4px' };
    },
    weekOfYear(d) {
      const dt = new Date(d + 'T00:00:00');
      const start = new Date(dt.getFullYear(), 0, 1);
      return Math.floor((dt - start) / (7 * 24 * 3600 * 1000));
    },
    // Aggregate one metric+group's time_series rows into one row's numbers.
    computeRow(results) {
      const byDate = {};
      for (const r of results) {
        const key = r.observation_date;
        if (!byDate[key]) byDate[key] = { observation_date: key, numerator: 0, denominator: 0, attribution: 0, numerator_efctv: null, denominator_efctv: null };
        byDate[key].numerator += r.numerator || 0;
        byDate[key].denominator += r.denominator || 0;
        byDate[key].attribution += r.attribution || 0;
        if (r.numerator_efctv != null) {
          byDate[key].numerator_efctv = (byDate[key].numerator_efctv || 0) + r.numerator_efctv;
          byDate[key].denominator_efctv = (byDate[key].denominator_efctv || 0) + (r.denominator_efctv || 0);
        }
      }
      const all = Object.values(byDate).sort((a, b) => a.observation_date.localeCompare(b.observation_date));
      const yr = d => parseInt(d.observation_date.substring(0, 4), 10);
      const rate = r => (r.denominator > 0 ? r.numerator / r.denominator : null);
      const efctvRate = r => (r.denominator_efctv > 0 ? (r.numerator_efctv || 0) / r.denominator_efctv : null);

      const cur = all.filter(r => yr(r) === 2026);
      const prior = all.filter(r => yr(r) === 2025);
      if (!cur.length || !prior.length) return { attributed: cur.length ? cur[cur.length - 1].attribution : null, current: cur.length ? rate(cur[cur.length - 1]) : null, lastYearEfctv: null, lastYearFinal: null, projected: null };

      const latest = cur[cur.length - 1];
      const attributed = latest.attribution || null;
      const current = rate(latest);
      const lastYearFinal = rate(prior[prior.length - 1]);
      const wk = this.weekOfYear(latest.observation_date);
      const priorSameWeek = prior.reduce((best, r) =>
        Math.abs(this.weekOfYear(r.observation_date) - wk) < Math.abs(this.weekOfYear(best.observation_date) - wk) ? r : best
      );
      const lastYearEfctv = efctvRate(priorSameWeek);
      let projected = null;
      if (current != null && lastYearEfctv != null && lastYearFinal != null) {
        projected = current + (lastYearFinal - lastYearEfctv);
      }
      return { attributed, current, lastYearEfctv, lastYearFinal, projected };
    },
    // One batched /run_multi call for ALL key metrics at a given entity_type.
    // Returns flat time-series rows tagged with metric_id + entity_name.
    async runMulti(entityType) {
      const { data } = await axios.post('api/impact_analytics/composable-metrics/run_multi/', {
        metric_ids: METRICS.map(m => m.id),
        entity_type: entityType,
        observation_year: 2026,
        time_series: true,
        group_by_contract: false,
      });
      return data?.results || [];
    },
    // Pivot the flat run_multi rows into { [metricId]: { groups: {label -> row}, tenant: row } }.
    // TWO calls total (group grain + tenant rollup), not two-per-metric.
    async fetchAllMetrics() {
      const entityType = GROUPINGS[this.activeGrouping].entityType;
      const [groupRows, tenantRows] = await Promise.all([
        this.runMulti(entityType),
        this.runMulti('Tenant'),
      ]);
      // group rows: split by metric_id, then by entity_name
      const groupByMetric = {};
      const namesSeen = new Set();
      for (const r of groupRows) {
        const mid = r.metric_id;
        const name = r.entity_name;
        if (mid == null || name == null) continue;
        namesSeen.add(name);
        ((groupByMetric[mid] = groupByMetric[mid] || {})[name] = groupByMetric[mid][name] || []).push(r);
      }
      // tenant rows: split by metric_id only
      const tenantByMetric = {};
      for (const r of tenantRows) {
        const mid = r.metric_id;
        if (mid == null) continue;
        (tenantByMetric[mid] = tenantByMetric[mid] || []).push(r);
      }
      const fixedOrder = GROUPINGS[this.activeGrouping].order;
      const labels = fixedOrder.length ? fixedOrder : [...namesSeen].sort();
      this.dynamicGroupLabels = fixedOrder.length ? [] : labels;
      const out = {};
      for (const m of METRICS) {
        const byName = groupByMetric[m.id] || {};
        const groupMap = {};
        for (const label of labels) {
          groupMap[label] = this.computeRow(byName[label] || []);
        }
        out[m.id] = { groups: groupMap, tenant: this.computeRow(tenantByMetric[m.id] || []) };
      }
      return out;
    },
    // One call: engagement-graph glance signals for every group of the active
    // entity_type. Keyed server-side by entity id (with .name); we re-key by
    // name to join the metric rows (which are keyed by label/name).
    async fetchEngagement() {
      const entityType = GROUPINGS[this.activeGrouping].entityType;
      try {
        const { data } = await axios.get('api/engagement-graph-nodes/cohort-summary/', {
          params: { entity_type: entityType },
        });
        const byName = {};
        const idByName = {};
        for (const [gid, g] of Object.entries(data?.groups || {})) {
          if (g?.name) {
            byName[g.name] = g;
            idByName[g.name] = Number(gid);   // label → grouping entity_id (for the rollup card)
          }
        }
        this.engagementByName = byName;
        this.entityIdByLabel = idByName;
      } catch (error) {
        console.error('Error fetching engagement signals:', error);
        this.engagementByName = {};
        this.entityIdByLabel = {};
      }
    },
    // The trajectory for one group + metric's initiative. At the cohort/pod
    // grain the initiative spans MANY practices (e.g. ~120 in a big cohort)
    // with a genuine spread, so a plurality/most-common read almost always
    // lands on a near-tie and paints every dot "mixed" (amber). Instead score
    // the distribution's CENTER OF MASS (ahead=+1, on_track=0, behind=-1;
    // 'mixed' nodes are themselves neutral) and bucket the mean: clearly
    // positive -> ahead, clearly negative -> behind, near zero -> on_track.
    // 'mixed' is reserved for a truly BIMODAL split (lots of both ahead AND
    // behind, little in the middle) — the only case where one dot honestly
    // can't summarize the group.
    trajectoryFor(label, metricId) {
      const metric = METRICS.find(m => m.id === metricId);
      const mix = this.engagementByName[label]?.trajectory_by_initiative?.[metric?.initiativeKey];
      if (!mix) return null;
      const ahead = mix.ahead || 0;
      const onTrack = mix.on_track || 0;
      const behind = mix.behind || 0;
      const mixedN = mix.mixed || 0;
      const total = ahead + onTrack + behind + mixedN;
      if (!total) return null;
      // The dot is the group's MODAL trajectory — the state the most practices
      // are in (plurality of ahead / on_track / behind). 'mixed' (amber) is
      // reserved for a genuine BIMODAL split — both ahead AND behind heavy with
      // a thin middle — the only case one dot honestly can't summarize. A near-
      // exact tie between the leader and runner-up (<5% of the group) falls to
      // on_track, the neutral read.
      const poles = (ahead + behind) / total;
      const tailBalance = Math.min(ahead, behind) / Math.max(1, ahead + behind);
      if (poles > 0.6 && tailBalance > 0.35) return 'mixed';
      const ranked = [['ahead', ahead], ['on_track', onTrack], ['behind', behind]]
        .sort((a, b) => b[1] - a[1]);
      const [topKey, topN] = ranked[0];
      const runnerN = ranked[1][1];
      if (topN - runnerN < 0.05 * total) return 'on_track';
      return topKey;
    },
    trajectoryDot(label, metricId) {
      const t = this.trajectoryFor(label, metricId);
      return TRAJECTORY[t] || NO_TRAJECTORY;
    },
    trajectoryTitle(label, metricId) {
      const dot = this.trajectoryDot(label, metricId);
      const eng = this.engagementByName[label];
      const parts = [`Engagement: ${dot.label}`];
      if (eng) {
        const b = eng.blockers?.open_total || 0;
        const a = (eng.actions?.open || 0) + (eng.actions?.in_progress || 0);
        if (b) parts.push(`${b} open blocker${b === 1 ? '' : 's'}`);
        if (a) parts.push(`${a} open action${a === 1 ? '' : 's'}`);
      }
      return parts.join(' · ');
    },
    async fetchData() {
      this.loading = true;
      try {
        const [byMetric] = await Promise.all([
          this.fetchAllMetrics(),
          this.fetchEngagement(),
        ]);
        this.rowsByMetric = byMetric;
      } catch (error) {
        console.error('Error fetching exec summary data:', error);
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    if (this.canView) this.fetchData();
  },
};
</script>
